import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import type { Change } from '../../shared/catalogue-diff'

export interface VersionChangesDialogProps {
  open: boolean
  /**
   * The staged version being described. ABSENT for the working draft, which has no number yet -
   * the title then names the draft rather than inventing a `v0`.
   */
  version?: number
  /** The version compared against; absent means this is the first one. */
  baseline?: number
  baselineIsLive?: boolean
  changes?: Change[]
  loading?: boolean
  error?: string
  onClose: () => void
  /** Opens the try-on preview for this version. Absent while its artwork is not loadable. */
  onPreview?: () => void
}

export default function VersionChangesDialog({
  open,
  version,
  baseline,
  baselineIsLive = false,
  changes,
  loading = false,
  error,
  onClose,
  onPreview,
}: VersionChangesDialogProps) {
  const { formatMessage } = useIntl()

  const bySection = (changes ?? []).reduce<Map<string, Change[]>>(
    (acc, change) => {
      const list = acc.get(change.sectionName) ?? []
      list.push(change)
      acc.set(change.sectionName, list)

      return acc
    },
    new Map(),
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth={true}>
      <DialogTitle>
        {version === undefined
          ? formatMessage({ id: 'avatarStudio.changes.draftTitle' })
          : formatMessage({ id: 'avatarStudio.changes.title' }, { n: version })}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {baseline === undefined
            ? formatMessage({ id: 'avatarStudio.changes.initial' })
            : formatMessage(
                {
                  id: baselineIsLive
                    ? 'avatarStudio.changes.againstLive'
                    : 'avatarStudio.changes.against',
                },
                { n: baseline },
              )}
        </DialogContentText>

        {loading && (
          <Stack alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size={24} data-testid="changes-loading" />
          </Stack>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {!loading &&
          !error &&
          baseline !== undefined &&
          changes?.length === 0 && (
            <Typography
              variant="body2"
              sx={{ mt: 2 }}
              data-testid="changes-empty"
            >
              {formatMessage(
                { id: 'avatarStudio.changes.none' },
                { n: baseline },
              )}
            </Typography>
          )}

        {!loading && !error && (changes?.length ?? 0) > 0 && (
          <Stack spacing={2} sx={{ mt: 2 }} data-testid="changes-list">
            {[...bySection.entries()].map(([sectionName, sectionChanges]) => (
              <Stack key={sectionName} spacing={0.5}>
                <Typography variant="subtitle2">{sectionName}</Typography>
                {sectionChanges.map((change, index) => (
                  <Typography key={`${change.kind}-${index}`} variant="body2">
                    {formatMessage(
                      { id: `avatarStudio.changes.${change.kind}` },
                      {
                        names: change.names.join(', '),
                        count: change.names.length,
                      },
                    )}
                  </Typography>
                ))}
              </Stack>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {formatMessage({ id: 'avatarStudio.editor.cancel' })}
        </Button>
        {onPreview && (
          <Button
            variant="contained"
            onClick={onPreview}
            data-testid="try-version"
          >
            {formatMessage({ id: 'avatarStudio.changes.tryVersion' })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
